/**
 * The Transaction Approval tab's own rules (IP-89; design spec 9.2), kept free
 * of React so node --test covers them (orders.test.js).
 *
 * The Core's approval is the only one now (IP-82): LIV reports a submission,
 * the Core deals it, and every order waits here for two kinds of people -
 * makers who review it (mandatory, with a comment, as many of them as care
 * to: IP-127) and a checker who approves it, sends it back or rejects it
 * (IP-85, IP-86, IP-130) and who is none of its reviewers (IP-128). The
 * server decides every one of those; what this module does is make sure the
 * page never builds a request it already knows the server will refuse:
 *
 *   - a review names the state_version each order showed the reviewer
 *     (expectedVersions) and carries one comment, held to the catalogue's own
 *     rule for it (3 to 1,000 characters, line breaks allowed); its body is
 *     put through the catalogue's own normaliser before it is sent. It sets
 *     no fee: fees are a percentage of the amount at the fund's rate
 *     (IP-136), and the reviewer's fee edit is gone;
 *   - a reason is held to the catalogue's own rule for it, one line included;
 *   - an approval carries both legs of a switch or transfer (G-4) and the
 *     state_version of every leg as it was shown (G-1), in batches that never
 *     split a pair;
 *   - no request is bigger than the Console API's firewall takes
 *     (MAX_BODY_BYTES): approvals are batched by size, and a review,
 *     send-back or reject too large for one request is refused here, saying
 *     how many would fit.
 *
 * Which orders can be reviewed and rejected is the server's own list
 * (approval/gates.js REVIEWABLE_STATES, REJECTABLE_STATES), read from the
 * domain so the page and the gate never disagree.
 */
import { REJECTABLE_STATES, REVIEWABLE_STATES } from '@lfoc/domain/approval/gates.js';
import { MAX_ORDERS, REVIEW_COMMENT_LENGTH, actionOf } from '@lfoc/domain/authority/catalogue.js';

export { MAX_ORDERS, REJECTABLE_STATES, REVIEWABLE_STATES };

/**
 * The largest request body the page sends. The Console API's web ACL runs
 * AWS's common rule set before anything else (62-console-api-waf.yaml,
 * IP-24), and its SizeRestrictions_BODY refuses a body over 8 KB (8,192
 * bytes) with a bare 403 the Console API never sees (spec 24.2.8). Counting
 * ids is not enough: an approval's expectedVersions doubles what its ids
 * weigh, and a review's expectedVersions does the same and adds a comment of
 * up to 1,000 characters - 3,000 bytes in Sinhala or Tamil. So every bulk
 * request is measured as it will be sent, and kept a margin below the rule.
 */
export const MAX_BODY_BYTES = 7680;

const encoder = new TextEncoder();

/** A request body's size as sent: its JSON, UTF-8 encoded (a Sinhala or Tamil letter is 3 bytes). */
export function bodyBytes(body) {
    return encoder.encode(JSON.stringify(body)).length;
}

/** The most of `count` orders whose request fits in MAX_BODY_BYTES; build(n) is the body for the first n of them. */
function mostThatFit(build, count) {
    let low = 0;
    let high = count;
    while (low < high) {
        const mid = Math.ceil((low + high) / 2);
        if (bodyBytes(build(mid)) <= MAX_BODY_BYTES) low = mid;
        else high = mid - 1;
    }
    return low;
}

/** Why this body cannot be sent as one request, in words the person can act on; null when it can. */
function sizeProblem(body, build, count, advice = '') {
    if (bodyBytes(body) <= MAX_BODY_BYTES) return null;
    return 'Too much for one request: the firewall refuses a request over 8 KB. Act on at most '
        + mostThatFit(build, count) + ' of these transactions at a time' + advice + '.';
}

/**
 * The states an order is still open in, oldest step first: VALIDATED through
 * READY, and HELD beside them. RECEIVED never rests in a committed row, and
 * everything after READY has been dealt or has ended. IP-177 (LWM1-476): an
 * order parked for LIV (AWAITING_LIV) is not in the Unit Registry - the
 * server leaves out every order the client has not finished in LIV - so it is
 * no state the screens offer.
 */
export const OPEN_STATES = Object.freeze([
    'VALIDATED', 'AWAITING_FUNDS', 'AWAITING_CLEARANCE', 'MATCHED_CLEARED', 'FROZEN', 'PRICED', 'READY', 'HELD'
]);

/**
 * How each state reads on the page. AWAITING_LIV is an instruction LIV was
 * still collecting signatures for (IP-83): never listed (IP-177), it is only
 * a step in a completed order's history.
 */
export const STATE_LABEL = Object.freeze({
    RECEIVED: 'Received',
    AWAITING_LIV: 'Waiting for LIV',
    VALIDATED: 'Validated',
    AWAITING_FUNDS: 'Awaiting Funds',
    AWAITING_CLEARANCE: 'Awaiting Clearance',
    MATCHED_CLEARED: 'Funds Cleared',
    FROZEN: 'Frozen (book closed)',
    PRICED: 'Priced',
    READY: 'Ready for Approval',
    APPROVED: 'Approved',
    ALLOCATED: 'Allocated',
    POSTED: 'Posted',
    REJECTED: 'Rejected',
    EXPIRED: 'Expired',
    CANCELLED: 'Cancelled',
    HELD: 'On Hold'
});

export const STATE_COLOUR = Object.freeze({
    RECEIVED: 'default',
    AWAITING_LIV: 'purple',
    VALIDATED: 'blue',
    AWAITING_FUNDS: 'gold',
    AWAITING_CLEARANCE: 'orange',
    MATCHED_CLEARED: 'cyan',
    FROZEN: 'geekblue',
    PRICED: 'lime',
    READY: 'green',
    APPROVED: 'green',
    ALLOCATED: 'green',
    POSTED: 'green',
    REJECTED: 'red',
    EXPIRED: 'default',
    CANCELLED: 'default',
    HELD: 'volcano'
});

export const ORDER_TYPE_LABEL = Object.freeze({
    SUB: 'Subscription',
    RED: 'Redemption',
    SWI_OUT: 'Switch Out',
    SWI_IN: 'Switch In',
    TRF_OUT: 'Transfer Out',
    TRF_IN: 'Transfer In'
});

/**
 * What the Review dialog calls a pair's other leg: the side of the deal it is
 * for the holder, with the pair's own word after it - a switch-out or a
 * transfer-out takes units away (a redemption), a switch-in or a transfer-in
 * adds them (a subscription).
 *
 * @param {string} orderType the OTHER leg's type
 * @returns {string}
 */
export function otherLegLabel(orderType) {
    switch (orderType) {
        case 'SWI_OUT': return 'Redemption transaction (switch out)';
        case 'SWI_IN': return 'Subscription transaction (switch in)';
        case 'TRF_OUT': return 'Redemption transaction (transfer out)';
        case 'TRF_IN': return 'Subscription transaction (transfer in)';
        default: return 'Other transaction';
    }
}

/**
 * Pending Transactions' type filter. A fund switch and a fund transfer are
 * a pair of legs, so choosing one lists both.
 */
export const TRANSACTION_TYPE_FILTERS = Object.freeze([
    Object.freeze({ value: 'SUB', label: 'Subscription', orderTypes: Object.freeze(['SUB']) }),
    Object.freeze({ value: 'RED', label: 'Redemption', orderTypes: Object.freeze(['RED']) }),
    Object.freeze({ value: 'SWITCH', label: 'Fund Switch', orderTypes: Object.freeze(['SWI_OUT', 'SWI_IN']) }),
    Object.freeze({ value: 'TRANSFER', label: 'Fund Transfer', orderTypes: Object.freeze(['TRF_OUT', 'TRF_IN']) })
]);

/** The order types a Pending Transactions type filter stands for, or none when it is cleared. */
export function orderTypesOf(txnType) {
    const chosen = TRANSACTION_TYPE_FILTERS.find((f) => f.value === txnType);
    return chosen ? [...chosen.orderTypes] : [];
}

/**
 * GET /orders for Pending transactions: every open state unless some are
 * chosen, and the legs of the chosen transaction type. An empty type is
 * every type. fundId and dealingDate travel only when set.
 */
export function pendingOrdersQuery({ states = [], txnType = null, fundId = null, dealingDate = null } = {}) {
    const orderTypes = orderTypesOf(txnType);
    return {
        states: states.length ? states : OPEN_STATES,
        ...(orderTypes.length ? { orderTypes } : {}),
        ...(fundId ? { fundId } : {}),
        ...(dealingDate ? { dealingDate } : {})
    };
}

/** Colour per leg, so the two legs of a switch or transfer read as a pair. */
export const ORDER_TYPE_COLOUR = Object.freeze({
    SUB: 'green', RED: 'red', SWI_OUT: 'orange', SWI_IN: 'blue', TRF_OUT: 'volcano', TRF_IN: 'geekblue'
});

/** orders/intake.js HOLD_REASON, as the operator reads it. */
export const HOLD_REASON_LABEL = Object.freeze({
    'HLD-002': 'Fund suspended',
    'HLD-003': 'Redemption restricted',
    'HLD-004': 'Holder dormant',
    LATE_DELIVERY: 'Late delivery from LIV',
    NO_PRICE_FOR_ESTIMATE: 'No price for the estimate',
    PAYOUT_ACCOUNT_UNREGISTERED: 'Payout account not registered'
});

/**
 * The checker's send-back and reject: a reason of 3 to 500 characters on one
 * line - the catalogue's rule (authority/catalogue.js checkerReason), counted
 * as it counts them, after the same trim. One line is the catalogue's
 * CONTROL: no C0 control or DEL (a line break, a tab), and no NEL, U+2028 or
 * U+2029 either - the reason travels to LIV as a reasons[].message of '^.+$',
 * which a Unicode line break (pasted from a PDF or Word) fails as surely as
 * \n.
 */
export const REASON_MIN = 3;
export const REASON_MAX = 500;
// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f\u0085\u2028\u2029]/;

/** pricing/reads.js MAX_GUARD_DATES: the most dates one guard call answers. */
export const MAX_GUARD_DATES = 31;

/** What is wrong with a reason, or null when the catalogue will accept it: it refuses exactly what this refuses. */
export function reasonProblem(value) {
    const trimmed = String(value ?? '').trim();
    if (trimmed.length < REASON_MIN) return 'Give a reason of at least ' + REASON_MIN + ' characters.';
    if (trimmed.length > REASON_MAX) return 'Keep the reason to ' + REASON_MAX + ' characters.';
    if (CONTROL.test(trimmed)) return 'Keep the reason on one line, with no line breaks, tabs or other control characters.';
    return null;
}

/**
 * IP-127: the reviewer's comment - one for every order the review names. 3 to
 * 1,000 characters once trimmed, counted as the catalogue and PostgreSQL
 * count them (code points: an emoji is one), on as many lines as the reviewer
 * likes: line breaks and tabs are allowed, any other control character is not
 * (authority/catalogue.js reviewComment, 0018's order_review_comment_text).
 */
export const COMMENT_MIN = REVIEW_COMMENT_LENGTH.min;
export const COMMENT_MAX = REVIEW_COMMENT_LENGTH.max;
// eslint-disable-next-line no-control-regex
const COMMENT_CONTROL = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f\u0085\u2028\u2029]/;

/** What is wrong with a review comment, or null when the catalogue will accept it: it refuses exactly what this refuses. */
export function commentProblem(value) {
    const trimmed = String(value ?? '').trim();
    const length = [...trimmed].length;
    if (length < COMMENT_MIN) return 'Write a comment of at least ' + COMMENT_MIN + ' characters: what you checked.';
    if (length > COMMENT_MAX) return 'Keep the comment to ' + COMMENT_MAX.toLocaleString('en-US') + ' characters.';
    if (COMMENT_CONTROL.test(trimmed)) return 'The comment may hold line breaks and tabs, but no other control characters.';
    return null;
}

/** The fee as the page shows it: a rate is a fraction (0.015000 is 1.5 per cent). Shared with every fee cell. */
export { ratePercent } from '../../../domain/feeRate.js';

/**
 * IP-203: what a review sends for the Apply fees checkboxes - feesWaived for
 * each leg whose box the reviewer changed (true: unticked, the fee waived;
 * false: ticked again), or null when nothing changed. A leg whose box is off
 * and disabled (no fee applies, a transfer) is never sent: the server would
 * refuse a waiver there (FEE_NOT_APPLICABLE).
 *
 * @param {object[]} orders the GET /orders rows being reviewed, each with its `fees` (the server's terms)
 * @param {Record<string, boolean>} checked orderId -> the box as the reviewer left it (ticked = true)
 * @returns {Record<string, boolean>|null}
 */
export function feesWaivedChange(orders, checked) {
    const out = {};
    for (const order of orders) {
        const fees = order?.fees;
        if (!fees?.applicable || !(order.orderId in (checked ?? {}))) continue;
        const waived = checked[order.orderId] !== true;
        // IP-204: against the box as shown - the leg's choice, or the fund's rule while nobody has chosen (the
        // server keeps a box left as shown, so only a change is sent).
        if (waived !== (fees.applied !== true)) out[order.orderId] = waived;
    }
    return Object.keys(out).length ? out : null;
}

/** An ORDER_REVIEW body as it will be sent: the ids, the version each showed, the comment and (IP-203) any Apply fees changes. */
function reviewBody(orders, comment, feesWaived = null) {
    return {
        orderIds: orders.map((o) => o.orderId),
        expectedVersions: Object.fromEntries(orders.map((o) => [o.orderId, o.stateVersion])),
        comment,
        ...(feesWaived ? { feesWaived } : {})
    };
}

/**
 * The ORDER_REVIEW body for these orders (IP-127; design 2.2) - {orderIds,
 * expectedVersions, comment}, put through the catalogue's own normaliser, so
 * a request the server would refuse on its shape is refused here with the
 * same words - or why it cannot be sent. expectedVersions is the
 * state_version each order had on the screen the reviewer read: an order
 * that moved since is skipped (APR-012) rather than reviewed at figures
 * nobody saw. Both legs of a switch or transfer must be among the orders (the
 * caller adds them: withPartners); the server skips a lone leg (APR-011). A
 * body over MAX_BODY_BYTES is refused here: the firewall would refuse it
 * whole.
 *
 * @param {object[]} orders the GET /orders rows being reviewed
 * @param {string} comment what the reviewer wrote
 * @param {{ feesWaived?: Record<string, boolean>|null }} [fees] IP-203: the Apply fees changes (feesWaivedChange)
 * @returns {{ body: { orderIds: string[], expectedVersions: Record<string, number>, comment: string,
 *   feesWaived?: Record<string, boolean> } | null, message: string|null }}
 */
export function reviewRequest(orders, comment, { feesWaived = null } = {}) {
    if (!orders.length) return { body: null, message: 'Choose a transaction to review.' };
    if (orders.length > MAX_ORDERS) return { body: null, message: 'Review at most ' + MAX_ORDERS + ' transactions at a time.' };
    const problem = commentProblem(comment);
    if (problem) return { body: null, message: problem };
    let body;
    try {
        body = actionOf('ORDER_REVIEW').params(reviewBody(orders, String(comment).trim(), feesWaived));
    } catch (error) {
        return { body: null, message: error.message };
    }
    const tooLarge = sizeProblem(body, (n) => reviewBody(orders.slice(0, n), body.comment), orders.length, ', or shorten the comment');
    return tooLarge ? { body: null, message: tooLarge } : { body, message: null };
}

/** The catalogue type of the checker's two other answers (ReasonModal). */
const REASON_TYPE = Object.freeze({ sendBack: 'ORDER_SEND_BACK', reject: 'ORDER_REJECT' });

/**
 * The ORDER_SEND_BACK or ORDER_REJECT body - {orderIds, reason}, put through
 * the catalogue's own normaliser - or why it cannot be sent as one request.
 *
 * @param {'sendBack'|'reject'} kind
 * @param {object[]} orders
 * @param {string} reason
 * @returns {{ body: { orderIds: string[], reason: string } | null, message: string|null }}
 */
export function reasonRequest(kind, orders, reason) {
    if (orders.length > MAX_ORDERS) return { body: null, message: 'Act on at most ' + MAX_ORDERS + ' transactions at a time.' };
    const trimmed = String(reason ?? '').trim();
    let body;
    try {
        body = actionOf(REASON_TYPE[kind]).params({ orderIds: orders.map((o) => o.orderId), reason: trimmed });
    } catch (error) {
        return { body: null, message: error.message };
    }
    const tooLarge = sizeProblem(body, (n) => ({ orderIds: orders.slice(0, n).map((o) => o.orderId), reason: trimmed }), orders.length, ', or shorten the reason');
    return tooLarge ? { body: null, message: tooLarge } : { body, message: null };
}

/**
 * Why these orders cannot go in one review, send-back or reject, or null -
 * asked before the dialog opens, so nobody writes a comment to be told to
 * start again: more than MAX_ORDERS, or a body over MAX_BODY_BYTES even at
 * its smallest (the shortest comment or reason the catalogue takes). The
 * request is measured again, as it will be sent, when it is submitted.
 *
 * @param {'review'|'sendBack'|'reject'} kind
 * @param {object[]} orders
 */
export function selectionProblem(kind, orders) {
    if (orders.length > MAX_ORDERS) return 'Act on at most ' + MAX_ORDERS + ' transactions at a time.';
    const build = kind === 'review'
        ? (n) => reviewBody(orders.slice(0, n), 'x'.repeat(COMMENT_MIN))
        : (n) => ({ orderIds: orders.slice(0, n).map((o) => o.orderId), reason: 'x'.repeat(REASON_MIN) });
    return sizeProblem(build(orders.length), build, orders.length);
}

/**
 * The selected orders with the other leg of every switch or transfer among
 * them added, when that leg is in the same queue: a pair is reviewed,
 * approved and rejected whole (G-4, design 2.1). `incomplete` lists the legs whose
 * partner is not in the queue - approving one alone would be skipped
 * (APR-011).
 *
 * @param {string[]} selectedIds
 * @param {object[]} queue the queue's orders
 * @returns {{ orders: object[], added: number, incomplete: object[] }}
 */
export function withPartners(selectedIds, queue) {
    const byId = new Map(queue.map((o) => [o.orderId, o]));
    const chosen = new Set(selectedIds.filter((id) => byId.has(id)));
    let added = 0;
    const incomplete = [];
    for (const id of [...chosen]) {
        const partner = byId.get(id).linkedOrderId;
        if (!partner) continue;
        if (!byId.has(partner)) {
            incomplete.push(byId.get(id));
        } else if (!chosen.has(partner)) {
            chosen.add(partner);
            added += 1;
        }
    }
    return { orders: queue.filter((o) => chosen.has(o.orderId)), added, incomplete };
}

/** An approval body with nothing in it, and what each leg adds to one: its id twice, its version, and two commas. */
const EMPTY_APPROVAL = bodyBytes({ orderIds: [], expectedVersions: {} });
const legBytes = (o) => bodyBytes({ orderIds: [o.orderId], expectedVersions: { [o.orderId]: o.stateVersion ?? null } }) - EMPTY_APPROVAL + 2;

/**
 * ORDER_APPROVE requests for these orders: {orderIds, expectedVersions}, at
 * most MAX_ORDERS orders and MAX_BODY_BYTES each (about 95 orders), a pair
 * never split across two (G-4). The versions are the ones the page rendered
 * (G-1).
 *
 * @param {object[]} orders
 * @param {{ max?: number, maxBytes?: number }} [limits]
 */
export function approvalBatches(orders, { max = MAX_ORDERS, maxBytes = MAX_BODY_BYTES } = {}) {
    const seen = new Set();
    const units = [];
    const byId = new Map(orders.map((o) => [o.orderId, o]));
    for (const order of orders) {
        if (seen.has(order.orderId)) continue;
        const unit = [order];
        seen.add(order.orderId);
        const partner = order.linkedOrderId && byId.get(order.linkedOrderId);
        if (partner && !seen.has(partner.orderId)) {
            unit.push(partner);
            seen.add(partner.orderId);
        }
        units.push(unit);
    }
    const batches = [];
    let current = [];
    let bytes = EMPTY_APPROVAL;
    for (const unit of units) {
        const adds = unit.reduce((sum, o) => sum + legBytes(o), 0);
        if (current.length && (current.length + unit.length > max || bytes + adds > maxBytes)) {
            batches.push(current);
            current = [];
            bytes = EMPTY_APPROVAL;
        }
        current.push(...unit);
        bytes += adds;
    }
    if (current.length) batches.push(current);
    return batches.map((legs) => ({
        orderIds: legs.map((o) => o.orderId),
        expectedVersions: Object.fromEntries(legs.map((o) => [o.orderId, o.stateVersion]))
    }));
}

/**
 * Approve every batch, asking again for what a request left notAttempted
 * because API Gateway's 29 s would have run out (Ruling R23: timedOut) - the
 * server finishes a unit it started and stops before the next. A request that
 * stoppedEarly (three failed units in a row: something systemic) is not
 * retried, and neither is one refused whole (PRICING_GUARD, a claim, the
 * firewall, the network): nothing after either is sent. What earlier batches
 * posted is kept, and notAttempted names every order left without an answer -
 * the rest of that request and every later batch - so the answer is always
 * the whole story; `error` is the refusal, when there was one.
 *
 * @param {(orderIds: string[], expectedVersions: object) => Promise<object>} approve api.orders.approve
 * @param {Array<{ orderIds, expectedVersions }>} batches approvalBatches()
 * @param {{ maxRounds?: number }} [options]
 * @returns {Promise<{ approved: object[], skipped: object[], failed: object[], notAttempted: string[], stoppedEarly: boolean,
 *   error: { code: string|null, message: string } | null }>}
 */
export async function approveInBatches(approve, batches, { maxRounds = 10 } = {}) {
    const out = { approved: [], skipped: [], failed: [], notAttempted: [], stoppedEarly: false, error: null };
    const later = (b) => batches.slice(b + 1).flatMap((batch) => batch.orderIds);
    for (let b = 0; b < batches.length; b += 1) {
        let pending = batches[b];
        for (let round = 0; pending; round += 1) {
            let result;
            try {
                result = await approve(pending.orderIds, pending.expectedVersions);
            } catch (error) {
                out.error = { code: error?.code ?? null, message: error?.message ?? String(error) };
                out.notAttempted.push(...pending.orderIds, ...later(b));
                return out;
            }
            out.approved.push(...(result.approved ?? []));
            out.skipped.push(...(result.skipped ?? []));
            out.failed.push(...(result.failed ?? []));
            const left = result.notAttempted ?? [];
            if (result.stoppedEarly) {
                out.stoppedEarly = true;
                out.notAttempted.push(...left, ...later(b));
                return out;
            }
            if (!result.timedOut || !left.length) break;
            if (round + 1 >= maxRounds) {
                out.notAttempted.push(...left);
                break;
            }
            pending = { orderIds: left, expectedVersions: Object.fromEntries(left.map((id) => [id, pending.expectedVersions[id]])) };
        }
    }
    return out;
}

/** The distinct dealing dates of these orders, in date order - what the pricing guard is asked about. */
export function dealingDatesOf(orders) {
    return [...new Set(orders.map((o) => o.dealingDate).filter(Boolean))].sort();
}

/** Split a list of dates into guard calls of at most MAX_GUARD_DATES. */
export function guardChunks(dates) {
    const chunks = [];
    for (let i = 0; i < dates.length; i += MAX_GUARD_DATES) chunks.push(dates.slice(i, i + MAX_GUARD_DATES));
    return chunks;
}

/**
 * The dates the guard says cannot be approved, date -> the funds with no
 * published price. ORDER_APPROVE refuses a WHOLE request while any date in it
 * is here (PRICING_GUARD, IP-62), so the page keeps those orders out of it.
 */
export function blockedDates(entries) {
    return new Map((entries ?? []).filter((e) => e.missingFunds?.length).map((e) => [e.date, e.missingFunds]));
}

const plural = (n, one, many = one + 's') => n + ' ' + (n === 1 ? one : many);

/**
 * What an action's answer means to the person who took it: a headline, its
 * tone, and one line per order it did not act on - in the server's own words
 * (a skip is never an error: the order moved, or another person got there
 * first). Pure, so every answer shape the routers give is tested.
 *
 * @param {'review'|'approve'|'sendBack'|'reject'} kind
 * @param {object} result the route's answer (approve: approveInBatches()'s)
 * @param {(orderId: string) => string} refOf an order's transaction id, for the lines
 * @returns {{ type: 'success'|'warning'|'error', title: string, lines: string[] }}
 */
export function summarise(kind, result, refOf = (orderId) => orderId) {
    const lines = [...(result?.skipped ?? []), ...(result?.failed ?? [])].map((s) => refOf(s.orderId) + ': ' + s.message);
    let title;
    let type = lines.length ? 'warning' : 'success';
    if (kind === 'review') {
        const n = result.reviewed?.length ?? 0;
        const them = n === 1 ? 'it' : 'them';
        title = plural(n, 'transaction') + ' reviewed; someone who has not reviewed ' + them + ' approves ' + them + '.';
    } else if (kind === 'approve') {
        title = plural(result.approved?.length ?? 0, 'transaction') + ' approved: units allocated and posted to the register.';
        const left = result.notAttempted ?? [];
        if (result.error) {
            // Refused whole, or no answer at all (the network): what that request did is not known here, so it is not guessed.
            type = 'error';
            const said = String(result.error.message ?? '').trim() || 'the request failed';
            lines.push('Approval stopped: ' + (/[.!?]$/.test(said) ? said : said + '.') + ' No answer for '
                + plural(left.length, 'transaction') + ': refresh, and approve again whichever is still waiting.');
        } else if (left.length) {
            type = 'error';
            lines.push(plural(left.length, 'transaction') + ' not attempted'
                + (result.stoppedEarly ? ': approval stopped after three failures in a row. Refresh and try again; if it happens again, the fault is not in the transactions.' : '. Refresh and approve them again.'));
        }
    } else if (kind === 'sendBack') {
        title = plural(result.sentBack?.length ?? 0, 'transaction') + ' sent back for review.';
    } else {
        const partners = result.partnersIncluded?.length ?? 0;
        title = plural(result.rejected?.length ?? 0, 'transaction') + ' rejected'
            + (partners ? ', including ' + plural(partners, 'linked transaction') + ' (the other leg of a switch or transfer, or the rest of an instruction LIV had not finished)' : '')
            + '. LIV has been told.';
        const refunds = result.refundRequired ?? [];
        if (refunds.length) {
            type = 'warning';
            lines.unshift('Refund required: the money for ' + refunds.map(refOf).join(', ') + ' has cleared and must be sent back through the cash refund flow.');
        }
    }
    return { type, title, lines };
}
